import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Star } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { api } from '../services/api';
import { AlertCircle } from 'lucide-react';
import { type PricingPlan, SubscriptionPlan, UpgradePreview } from '../types';

export default function Pricing() {
  const { theme } = useTheme();
  const { font } = useFont();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setIsPolling } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [showAuthMessage, setShowAuthMessage] = useState(false);
  const [showDowngradeMessage, setShowDowngradeMessage] = useState(false);
  const isUpgrade = searchParams.get('upgrade') === 'true';
  const isCanceled = searchParams.get('subscription') === 'canceled';
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showFreeHint, setShowFreeHint] = useState(true);
  const [upgradePreview, setUpgradePreview] = useState<{ plan: PricingPlan; preview: UpgradePreview } | null>(null);

  // Stop polling when component unmounts
  useEffect(() => {
    return () => {
      setIsPolling(false);
    };
  }, []);

  const isCurrentPlan = (planId: string): boolean => {
    return user?.subscription === planId ||
           (billingPeriod === 'yearly' && user?.subscription === getPlanId(planId as SubscriptionPlan, true));
  };

  const canUpgradeToPlan = (planId: string): boolean => {
    if (!user?.subscription) return true;

    // Get base plan IDs (without monthly/annually suffix)
    const currentBasePlan = user.subscription.split('_')[0];
    const newBasePlan = planId.split('_')[0];

    // Get plan indices from the plans array
    const currentPlanIndex = plans.findIndex(p => p.id.startsWith(currentBasePlan));
    const newPlanIndex = plans.findIndex(p => p.id.startsWith(newBasePlan));

    return newPlanIndex > currentPlanIndex;
  };

  const getSubscriptionLabel = (type: string): string => {
    switch (type) {
      case 'DETERMINED_MONTHLY':
        return 'Déterminé (mensuel)';
      case 'DETERMINED_ANNUALLY':
        return 'Déterminé (annuel)';
      case 'MOTIVATED_MONTHLY':
        return 'Motivé (mensuel)';
      case 'MOTIVATED_ANNUALLY':
        return 'Motivé (annuel)';
      case 'SHY_MONTHLY':
        return 'Timide (mensuel)';
      case 'SHY_ANNUALLY':
        return 'Timide (annuel)';
      default:
        return type;
    }
  };

  const plans = useMemo<PricingPlan[]>(() => [
    {
      id: SubscriptionPlan.SHY_MONTHLY,
      name: 'Timide',
      price: 15,
      description: 'Pour débuter en douceur',
      features: [
        'Accès à tous les cours',
        '10 questions QCM par jour',
        'Suivi de progression personnalisé',
        'Support par email',
        'Paiement mensuel ou annuel'
      ],
      yearlyPrice: 165,
    },
    {
      id: SubscriptionPlan.MOTIVATED_MONTHLY,
      name: 'Motivé',
      price: 22,
      description: 'Pour une préparation intensive',
      features: [
        'Accès à tous les cours',
        'QCM illimités',
        'Suivi de progression personnalisé',
        'Support prioritaire',
        'Paiement mensuel ou annuel'
      ],
      yearlyPrice: 240,
      recommended: true,
    },
    {
      id: SubscriptionPlan.DETERMINED_MONTHLY,
      name: 'Déterminé',
      price: 35,
      description: 'Pour une réussite garantie',
      features: [
        'Accès à tous les cours',
        'QCM illimités',
        'Support prioritaire',
        'Version annuelle uniquement : **2 RDV individuels offerts**'
      ],
      yearlyPrice: 450
    }
  ], []);

  const oneShot = [
    {
      id: 'visio-individuelle',
      name: 'Visio individuelle',
      price: 50,
      description: 'Session personnalisée',
      features: [
        'Accompagnement sur-mesure',
        'Choix du thème libre',
        'Questions illimitées',
        'Conseils personnalisés',
        'Durée : 1 heure'
      ]
    }
  ];

  const getPlanId = (baseId: SubscriptionPlan, isYearly: boolean): SubscriptionPlan => {
    if (isYearly) {
      switch (baseId) {
        case SubscriptionPlan.SHY_MONTHLY:
          return SubscriptionPlan.SHY_ANNUALLY;
        case SubscriptionPlan.MOTIVATED_MONTHLY:
          return SubscriptionPlan.MOTIVATED_ANNUALLY;
        case SubscriptionPlan.DETERMINED_MONTHLY:
          return SubscriptionPlan.DETERMINED_ANNUALLY;
        default:
          return baseId;
      }
    }
    return baseId;
  };

  const handleUpgradePreview = async (plan: PricingPlan) => {
    try {
      const preview = await api.previewUpgrade(
        getPlanId(plan.id as SubscriptionPlan, billingPeriod === 'yearly')
      );
      setUpgradePreview({ plan, preview });
    } catch (error) {
      console.error('Failed to get upgrade preview:', error);
      alert('Une erreur est survenue lors du calcul du montant. Veuillez réessayer.');
    }
  };

  const handleSubscribe = async (plan: PricingPlan) => {
    try {
      if (!user) {
        setShowAuthMessage(true);
        setTimeout(() => setShowAuthMessage(false), 5000);
        return;
      }

      // If it's an upgrade, show preview first
      if (isUpgrade && !upgradePreview) {
        await handleUpgradePreview(plan);
        return;
      }

      // If it's an upgrade and the user tries to subscribe to a lower tier plan
      if (isUpgrade) {
        const currentPlanIndex = plans.findIndex(p => p.id === user.subscription);
        const newPlanIndex = plans.findIndex(p => p.id === plan.id);
        if (newPlanIndex < currentPlanIndex) {
          alert('Vous ne pouvez pas passer à une formule inférieure. Pour réduire votre abonnement, veuillez d\'abord le résilier.');
          return;
        }
      }

      const response = await api.createSubscription({
        plan: getPlanId(plan.id as SubscriptionPlan, billingPeriod === 'yearly'),
        successUrl: `${window.location.origin}/dashboard?subscription=success&upgrade=${isUpgrade}`,
        cancelUrl: `${window.location.origin}/tarifs?subscription=canceled`
      });

      if (!user.subscription) {
        // First time subscription - redirect to checkout
        if (response.checkout_url) {
          window.location.href = response.checkout_url;
        }
      } else if (response.payment_required) {
        // Upgrade with payment required - redirect to invoice
        window.open(response.invoice_url, '_blank');
        setIsProcessingPayment(true);
        setIsPolling(true);
      } else {
        // Subscription updated successfully, redirect to dashboard
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to create subscription:', error);
      alert('Une erreur est survenue lors de la création de l\'abonnement. Veuillez réessayer.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: font.family }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {showFreeHint && !user && (
  <div className="fixed top-20 right-12 z-50">
    <div className="w-full flex justify-center mb-1">
      <svg
          className="w-10 h-10 text-rose-500 transform"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
      >
        <path d="M12 19V6"/>
        <path d="M6 12l6-6l6 6"/>
      </svg>
    </div>

    <div
        className={`bg-${theme.secondary} text-${theme.text} border border-${theme.primary} px-4 py-3 rounded-lg shadow-lg min-w-[260px]`}
    >
      <div className="font-semibold text-sm">
        Commencez gratuitement pendant 7 jours&nbsp;!
      </div>
      <div className="text-xs text-gray-700">
        Créez un compte pour accéder au contenu gratuit !
      </div>

      <button
        className="mt-2 text-xs underline text-gray-600"
        onClick={() => setShowFreeHint(false)}
      >
        Ne plus afficher
      </button>
    </div>
  </div>
)}




        {showAuthMessage && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
            <div className={`bg-${theme.secondary} border border-${theme.primary} text-${theme.text} px-6 py-3 rounded-lg shadow-lg`}>
              Pour vous abonner, veuillez d'abord vous connecter en cliquant sur "Connexion" en haut à droite
            </div>
          </div>
        )}

        <div className="text-center mb-12">
          <h1 className={`text-4xl font-bold text-${theme.text} mb-4`}>
            Nos formules d'accompagnement
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Des formules adaptées à vos besoins pour réussir votre VAE
          </p>
        </div>

        {isUpgrade && (
          <div className={`mb-8 p-4 rounded-lg bg-${theme.secondary} border border-${theme.primary}`}>
            <p className="text-center text-gray-700">
              Vous êtes actuellement sur la formule <strong>{getSubscriptionLabel(user?.subscription || '')}</strong>.
              En changeant de formule, votre nouvel abonnement commencera immédiatement.
            </p>
          </div>
        )}

        <div className="text-center mb-8">
          <div className="mt-8 inline-flex items-center p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                billingPeriod === 'monthly'
                  ? `bg-white shadow text-${theme.text}`
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Mensuel
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                billingPeriod === 'yearly'
                  ? `bg-white shadow text-${theme.text}`
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Annuel
              <span className={`ml-2 text-xs py-0.5 px-2 rounded-full bg-${theme.secondary} text-${theme.text}`}>
                -17%
              </span>
            </button>
          </div>
        </div>

        {/* Abonnements */}
        <h2 className="text-2xl font-bold mb-8 text-center">Abonnements</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`
                relative bg-white rounded-2xl shadow-sm overflow-hidden
                ${plan.recommended ? `border-2 border-${theme.primary}` : 'border border-gray-200'}
              `}
            >
              {plan.recommended && (
                <div className={`absolute top-0 right-0 bg-${theme.primary} text-white px-3 py-1 rounded-bl-lg`}>
                  <div className="flex items-center text-sm font-medium">
                    <Star className="w-4 h-4 mr-1" />
                    Recommandé
                  </div>
                </div>
              )}

              <div className="p-8">
                <h2 className="text-2xl font-bold mb-2">{plan.name}</h2>
                <p className="text-gray-600 mb-6">{plan.description}</p>

                <div className="mb-8">
                  <div className="flex items-end">
                    <span className="text-4xl font-bold">
                      {billingPeriod === 'monthly' ? plan.price : plan.yearlyPrice}€
                    </span>
                    <span className="text-gray-500 ml-2">
                      /{billingPeriod === 'monthly' ? 'mois' : 'an'}
                    </span>
                  </div>
                  {billingPeriod === 'yearly' && (
                    <p className="text-sm text-gray-500 mt-1">
                      soit {(plan.yearlyPrice / 12).toFixed(2)}€/mois
                    </p>
                  )}
                </div>

                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={isCurrentPlan(plan.id) || (isUpgrade && !canUpgradeToPlan(plan.id))}
                  onMouseEnter={() => {
                    if (isUpgrade && !canUpgradeToPlan(plan.id)) {
                      setShowDowngradeMessage(true);
                    }
                  }}
                  onMouseLeave={() => setShowDowngradeMessage(false)}
                  className={`
                    w-full py-3 px-6 rounded-lg font-medium mb-8 transition-all
                    ${isCurrentPlan(plan.id) || (isUpgrade && !canUpgradeToPlan(plan.id))
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : plan.recommended
                          ? `bg-${theme.primary} text-white hover:opacity-90`
                          : `border-2 border-${theme.primary} text-${theme.text} hover:bg-${theme.secondary}`
                    }
                  `}
                >
                  {isCurrentPlan(plan.id)
                    ? 'Votre formule actuelle'
                    : isUpgrade
                      ? canUpgradeToPlan(plan.id)
                        ? 'Passer à cette formule'
                        : 'Formule inférieure'
                      : isCanceled
                        ? 'Réessayer'
                        : 'Commencer maintenant'
                  }
                </button>

                <ul className="space-y-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className={`w-5 w-5 mr-3 text-${theme.primary} flex-shrink-0 mt-0.5`} />
                      <span dangerouslySetInnerHTML={{
                        __html: feature.replace(/\*\*(.*?)\*\*/g, `<strong class="text-${theme.primary}">$1</strong>`)
                      }} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Services à l'unité */}
        <h2 className="text-2xl font-bold mt-16 mb-8 text-center">Services à l'unité</h2>
<div className="grid grid-cols-1 md:grid-cols-1 justify-items-center gap-8 max-w-4xl mx-auto">          {oneShot.map((service) => (
    <div
        key={service.id}
        className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 w-full max-w-md"
    >
      <div className="p-8">
        <h2 className="text-2xl font-bold mb-2">{service.name}</h2>
        <p className="text-gray-600 mb-6">{service.description}</p>

        <div className="mb-8">
          <div className="flex items-end">
            <span className="text-4xl font-bold">{service.price}€</span>
            <span className="text-gray-500 ml-2">/session</span>
          </div>
        </div>

        <button
            className={`
                    w-full py-3 px-6 rounded-lg font-medium mb-8 transition-all border-2 text-center
                    border-${theme.primary} text-${theme.text} hover:bg-${theme.secondary} block
                  `}
            onClick={() => {
              window.open(
                  service.id === 'visio-groupe'
                      ? 'https://calendly.com/loustaletformation/formation-de-groupe-abonne-clone'
                      : 'https://calendly.com/loustaletformation/rendez-vous-individuel-abonne-clone',
                  '_blank'
              );
            }}
        >
          Réserver une session
        </button>

        <ul className="space-y-4">
          {service.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <Check className={`w-5 h-5 mr-3 text-${theme.primary} flex-shrink-0 mt-0.5`}/>
                <span>{feature}</span>
              </li>
          ))}
        </ul>
      </div>
    </div>
))}
</div>

        <div className="mt-12 text-center">
          <div className={`max-w-2xl mx-auto bg-${theme.secondary} rounded-xl p-6`}>
            <h3 className={`text-xl font-semibold text-${theme.text} mb-2`}>
              Des questions ?
            </h3>
            <p className="text-gray-600 mb-4">
              Notre équipe est là pour vous aider dans votre choix de formation
            </p>
            <div className="flex justify-center">
              <a
                href="mailto:loustaletformation@gmail.com"
                className={`flex items-center px-4 py-2 rounded-lg bg-white text-${theme.text} hover:bg-${theme.primary} hover:text-black transition-colors`}
              >
                <span className="font-medium">loustaletformation@gmail.com</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Downgrade Message Modal */}
      {showDowngradeMessage && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
          <div className={`bg-${theme.secondary} border border-${theme.primary} text-${theme.text} px-6 py-3 rounded-lg shadow-lg max-w-md`}>
            Pour passer à une formule inférieure, vous devez d'abord résilier votre abonnement actuel depuis votre profil.
          </div>
        </div>
      )}

      {/* Upgrade Preview Modal */}
      {upgradePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-8">
            <div className="flex items-center justify-center mb-4">
              <div className={`w-12 h-12 rounded-full bg-${theme.secondary} flex items-center justify-center`}>
                <AlertCircle className={`h-6 w-6 text-${theme.text}`} />
              </div>
            </div>
            <h3 className="text-xl font-bold text-center mb-2">Confirmer le changement de formule</h3>
            <div className="space-y-4 mb-8">
              <p className="text-gray-600">
                Vous allez passer à la formule <strong>{upgradePreview.plan.name}</strong>.
              </p>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg bg-${theme.secondary}`}>
                  <p className="font-medium mb-2">Montant à payer aujourd'hui :</p>
                  <p className="text-2xl font-bold">{upgradePreview.preview.amount_due_now}€</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Ce montant tient compte du prorata de votre abonnement actuel
                  </p>
                </div>
                <div className={`p-4 rounded-lg bg-${theme.secondary}`}>
                  <p className="font-medium mb-2">Prochain paiement :</p>
                  <p className="text-2xl font-bold">{upgradePreview.preview.next_payment_amount}€</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {upgradePreview.preview.next_payment_date
                      ? `Le ${new Date(upgradePreview.preview.next_payment_date).toLocaleDateString('fr-FR')}`
                      : 'Date à confirmer'
                    }
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={() => setUpgradePreview(null)}
                disabled={isProcessingPayment}
                className={`px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors ${
                  isProcessingPayment ? 'hidden' : ''
                }`}
              >
                Annuler
              </button>
              <button
                onClick={() => handleSubscribe(upgradePreview.plan)}
                disabled={isProcessingPayment}
                className={`px-6 py-2 bg-${theme.primary} text-white rounded-lg hover:opacity-90 transition-opacity relative min-w-[120px] ${
                  isProcessingPayment ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isProcessingPayment ? (
                  <>
                    <span className="opacity-0">Chargement</span>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className={`w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin`} />
                    </div>
                  </>
                ) : (
                  'Confirmer'
                )}
              </button>
              <button
                onClick={() => {
                  setIsProcessingPayment(false);
                  setIsPolling(false);
                  setUpgradePreview(null);
                }}
                className={`px-4 py-2 text-red-600 hover:text-red-700 transition-colors ${
                  isProcessingPayment ? '' : 'hidden'
                }`}
              >
                Annuler le paiement
              </button>
            </div>
            {isProcessingPayment && (
              <div className="text-center text-sm text-gray-600 mt-6">
                <p>En attente de paiement...</p>
                <p className="mt-1">Vous pouvez fermer la fenêtre de paiement et annuler l'opération à tout moment.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}